<template>
  <BaseDialog centered contentClass="create-quote-dialog" ref="dialog" :show-actions="true">
    <template slot="title">{{ $t('quotes.create-and-sync-quote') }}</template>
    <template slot="content">
      <div>
        <div class="create-quote-dialog__potentiel-bidder">
          <div class="create-quote-dialog__subtitle">{{ $t('quotes.data-table-titles.potential-bidder') }}</div>
          <BasePotentialBidder v-if="potentialBidder" :potentialBidder="potentialBidder" :key="potentialBidder.id" />
        </div>
        <div class="create-quote-dialog__quote">
          <div class="create-quote-dialog__subtitle">
            {{ $t('title.quotes') }}
            <div class="create-quote-dialog__dropdown">
              <BaseSelect
                isSmall
                name="profile"
                :value="selectedSyncProfile"
                :items="availableSyncProfiles"
                item-text="name"
                item-value="profile"
                @change="onOpportunitySyncProfileChange"
              />
            </div>
          </div>
          <div class="create-quote-dialog__external-entity-version">
            <div v-if="!hasAvailableSyncProfiles" class="sync-opportunity__external-fields">
              <span class="create-quote-dialog__warning">{{
                $t('quote-sync.unsynchronized-opportunity-warning')
              }}</span>
            </div>
            <div
              v-else-if="hasFieldMappings && displayableFieldMappings.length > 0"
              class="sync-opportunity__external-fields"
            >
              <div class="sync-opportunity__external-fields--subtitle">
                {{ $t('page.opportunity-details.sync-validation.sync-details') }}
              </div>
              <SyncFieldMappings
                :key="fieldMappingsKey"
                :field-mappings="quoteFieldMappings"
                :values="customFieldValues"
                :sync-profile-id="selectedSyncProfile.id"
                :sync-profile="selectedSyncProfile"
                :is-create="true"
                :default-queries="defaultQueries"
                :create-defaults="createDefaults"
                @input="customFieldValues = $event"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="create-quote-dialog__actions">
        <FormButton is-outlined class="create-quote-dialog__cancel" @click.native="cancelClick">
          {{ $t('actions.cancel') }}
        </FormButton>
        <FormButton @click.native="onSubmit" type="submit" :disabled="!hasAvailableSyncProfiles">
          <span>{{ $t('actions.confirm') }} {{ $t('basic-words.and') }} {{ $t('actions.synchronize') }}</span>
        </FormButton>
      </div>
    </template>
  </BaseDialog>
</template>

<script>
import BaseDialog from '@/components/base/BaseDialog';
import BasePotentialBidder from '@/components/base/BasePotentialBidder';
import BaseSelect from '@/components/base/BaseSelect';
import FormButton from '@/components/form/FormButton';
import SyncFieldMappings from '@/components/sync/SyncFieldMappings';

import { buildLookupFieldDefaults, filterFieldMappingsForMode, supportsFieldMappings } from '@/util/sync-profile-util';
import { formatPhoneNumber } from '@/util/phone-util';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'CreateQuoteDialog',
  components: {
    BaseDialog,
    BasePotentialBidder,
    BaseSelect,
    FormButton,
    SyncFieldMappings,
  },
  data() {
    return {
      potentialBidder: null,
      selectedSyncProfile: null,
      customFieldValues: {},
      fieldMappingsKey: 0,
      defaultQueries: {},
      createDefaults: {},
    };
  },
  computed: {
    ...mapGetters('opportunitySyncProfiles', ['opportunitySyncProfiles']),
    ...mapGetters('opportunitySync', ['opportunitySyncs']),
    ...mapGetters('opportunity', ['opportunity']),
    ...mapGetters('syncFieldMappings', { syncFieldMappingsList: 'fieldMappings' }),
    quoteFieldMappings() {
      return [...this.syncFieldMappingsList].sort((a, b) => a.displayOrder - b.displayOrder);
    },
    displayableFieldMappings() {
      return filterFieldMappingsForMode(this.quoteFieldMappings, true);
    },
    hasFieldMappings() {
      return this.selectedSyncProfile && supportsFieldMappings(this.selectedSyncProfile.type);
    },
    activeSyncProfiles() {
      return this.opportunitySyncProfiles.filter((profile) => profile.active);
    },
    availableSyncProfiles() {
      // Only profiles whose opportunity already exists in the CRM (externalOpportunityId set); a record
      // alone (e.g. a failed sync) is not a valid target.
      return this.activeSyncProfiles.filter((syncProfile) =>
        this.opportunitySyncs.some(
          (oppSync) =>
            oppSync.profileId === syncProfile.id &&
            oppSync.opportunityId === this.opportunity.id &&
            oppSync.externalOpportunityId,
        ),
      );
    },
    hasAvailableSyncProfiles() {
      return this.availableSyncProfiles && this.availableSyncProfiles.length > 0;
    },
    isSelectedProfileSyncPending() {
      // The store holds entries for other opportunities too (opportunities page), so match on both keys.
      const oppSync = this.opportunitySyncs.find(
        (sync) => sync.profileId === this.selectedSyncProfile?.id && sync.opportunityId === this.opportunity.id,
      );
      return oppSync ? oppSync.status === 'IN_PROGRESS' : false;
    },
  },
  methods: {
    ...mapActions('quotes', ['createQuote']),
    ...mapActions('quoteSync', ['syncQuote']),
    ...mapActions('syncFieldMappings', ['getFieldMappings']),
    async openDialog(potentialBidder) {
      this.potentialBidder = potentialBidder;
      this.selectedSyncProfile = this.availableSyncProfiles[0];
      this.customFieldValues = {};
      this.defaultQueries = {};
      this.createDefaults = {};
      this.fieldMappingsKey++;
      this.$refs.dialog.openModal();

      if (this.selectedSyncProfile && this.hasFieldMappings) {
        const fieldMappings = await this.getFieldMappings({
          syncProfileId: this.selectedSyncProfile.id,
          scope: 'quote',
          formOnly: true,
        });
        this.applyLookupDefaults(fieldMappings);
      }
    },
    async onOpportunitySyncProfileChange(profileName) {
      if (!this.availableSyncProfiles) return;

      const syncProfile = this.availableSyncProfiles.find((p) => p.name === profileName);
      this.selectedSyncProfile = syncProfile;
      this.customFieldValues = {};
      this.defaultQueries = {};
      this.createDefaults = {};
      this.fieldMappingsKey++;

      if (this.selectedSyncProfile && this.hasFieldMappings) {
        const fieldMappings = await this.getFieldMappings({
          syncProfileId: this.selectedSyncProfile.id,
          scope: 'quote',
          formOnly: true,
        });
        this.applyLookupDefaults(fieldMappings);
      }
    },
    applyLookupDefaults(fieldMappings) {
      if (!this.potentialBidder) {
        this.defaultQueries = {};
        this.createDefaults = {};
        return;
      }

      const contact = this.potentialBidder.contact_points?.[0] || {};
      const nameParts = (contact.name || '').split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      const { defaultQueries, createDefaults } = buildLookupFieldDefaults(fieldMappings, {
        accountQuery: this.potentialBidder.organization_name || '',
        accountCreate: { name: this.potentialBidder.organization_name || '' },
        contactQuery: contact.name || '',
        contactCreate: {
          firstName,
          lastName,
          email: contact.email || '',
          phone: contact.telephone ? formatPhoneNumber(contact.telephone) : '',
        },
      });

      this.defaultQueries = defaultQueries;
      this.createDefaults = createDefaults;
    },
    closeDialog() {
      this.$refs.dialog.isModalOpen = false;
    },
    onSubmit() {
      if (this.isSelectedProfileSyncPending) {
        this.$toast.warning(this.$t('quote-sync.opp-sync-pending-warning'));
        return;
      }

      const contact = this.potentialBidder.contact_points?.[0] || {};
      const contactName = contact.name || '';
      const nameParts = contactName.split(' ');

      this.createQuote({
        opportunityId: this.opportunity.id,
        payload: {
          name: this.opportunity.name,
          quoteNumber: null,
          description: null,
          accountName: this.potentialBidder.organization_name || null,
          accountAddress: null,
          contactFirstName: nameParts[0] || null,
          contactLastName: nameParts.slice(1).join(' ') || null,
          contactPhone: contact.telephone || null,
          contactEmail: contact.email || null,
          contactFax: null,
          expirationDate: null,
          totalAmount: null,
          currency: 'CAD',
          status: null,
          sourceEntity: 'potential_bidder',
          sourceId: this.potentialBidder.id,
        },
      }).then((quote) => {
        this.syncQuote({
          opportunitySyncProfileId: this.selectedSyncProfile.id,
          quoteId: quote.id,
          fieldValues: this.customFieldValues,
        })
          .then(() => {
            this.$emit('quote-created');
            this.closeDialog();
          })
          .catch(() => {
            this.$toast.error(this.$t('quote-sync.quote-sync-error'));
            this.$emit('quote-created');
            this.closeDialog();
          });
      });
    },
    cancelClick() {
      this.closeDialog();
    },
  },
};
</script>

<style lang="scss">
.base-dialog__create-quote-dialog {
  height: auto;
  width: 750px;
  position: relative;

  .base-dialog__content {
    padding: 32px;
    font-size: 18px;
    background: $color-white !important;
  }

  .close-icon__container {
    display: none;
  }
}

.create-quote-dialog {
  &__actions {
    margin-top: 32px;
    justify-content: flex-end;
    display: flex;
  }

  &__cancel {
    margin-right: 12px;
  }

  &__subtitle {
    padding-bottom: 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__potentiel-bidder {
    padding-bottom: 24px;
  }

  &__dropdown {
    width: 320px;
  }

  &__external-entity-version {
    margin-top: 8px;
  }

  &__warning {
    color: $color-error;
    font-size: 14px;
  }
}
</style>
